import React, { useState } from 'react';
import { Row, Col, Button, Form, Accordion, Card } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { InputSelect } from '../../components';
import { debounceIndexEmployeeDncRequest } from '../../requests/trainingPrograms';
import { debounceIndexJobManagementsRequest } from '../../requests/jobManagements';
import { debounceIndexJobTitleRequest } from '../../requests/jobTitles';
import { debounceIndexCostCentersRequest } from '../../requests/costCenters';
import { debounceIndexEmployeesRequest } from '../../requests/employees';
import { downloadFile } from '../../services/utils';
import { periods } from '../DNC/FormOptions';
import TrainingProgramDatatable from './TrainingProgramDatatable';

const TrainingProgramIndex = () => {
  const [query, setQuery] = useState({});
  const [onRequest, setOnRequest] = useState(false);
  const [employeeDncs, setEmployeeDncs] = useState([]);
  const [amount, setAmount] = useState(0);
  const [yearsOptions, setYearsOptions] = useState([]);

  // Filter states
  const [year, setYear] = useState(null);
  const [period, setPeriod] = useState(null);
  const [rut, setRut] = useState('');
  const [employees, setEmployees] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [jobManagements, setJobManagements] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [directLeaders, setDirectLeaders] = useState([]);

  const [activeAccordion, setActiveAccordion] = useState('0');

  const dispatch = useDispatch();

  const handleIndexRequest = async (params, format, responseType = 'json') => {
    setOnRequest(true);
    const sendParams = {
      filter_year: year ? year.value : null,
      filter_period: period ? period.value : null,
      filter_rut: rut || null,
      filter_employee_ids: employees.length > 0 ? employees : null,
      filter_job_title: jobTitles.length > 0 ? jobTitles : null,
      filter_job_management: jobManagements.length > 0 ? jobManagements : null,
      filter_cost_center: costCenters.length > 0 ? costCenters : null,
      filter_direct_leader_ids: directLeaders.length > 0 ? directLeaders : null,
      ...params
    };

    // Remove null/undefined/empty from params
    Object.keys(sendParams).forEach(key => {
      if (sendParams[key] === null || sendParams[key] === undefined || sendParams[key] === '') {
        delete sendParams[key];
      }
    });

    setQuery({ ...sendParams });
    debounceIndexEmployeeDncRequest({
      dispatch,
      params: sendParams,
      format,
      responseType,
      successCallback: response => {
        if (format === '.xlsx') {
          downloadFile(response);
        } else {
          const data = response?.data?.data || [];
          const metadata = response?.data?.metadata || { amount: 0 };
          setEmployeeDncs(data);
          setAmount(metadata.amount);
          if (metadata.years) {
            setYearsOptions(metadata.years.map(y => ({ label: y.toString(), value: y })));
          }
        }
      },
      callback: () => setOnRequest(false)
    });
  };

  const yearOptions = () => {
    if (yearsOptions.length > 0) return yearsOptions;
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 7 }, (_, i) => ({
      label: (currentYear + i - 2).toString(),
      value: currentYear + i - 2
    }));
    return years;
  };

  const resultFetchData = (response, type) => {
    const result = response.data.data;
    return result.map(element => ({
      ...element,
      label: `${type ? `${element.code} - ` : ''} ${element.name}`,
      value: element.id
    }));
  };

  const fetchJobManagements = (inputValue, callback) => {
    debounceIndexJobManagementsRequest({
      dispatch,
      params: { actives: true, query: inputValue, sort_column: 'name', display_length: 100 },
      successCallback: data => callback(resultFetchData(data))
    });
  };

  const fetchJobTitles = (inputValue, callback) => {
    debounceIndexJobTitleRequest({
      dispatch,
      params: { actives: true, query: inputValue, sort_column: 'name', display_length: 100 },
      successCallback: data => callback(resultFetchData(data, true))
    });
  };

  const fetchCostCenters = (inputValue, callback) => {
    debounceIndexCostCentersRequest({
      dispatch,
      params: { actives: true, name: inputValue, sort_column: 'name', display_length: 100 },
      successCallback: data => callback(resultFetchData(data))
    });
  };

  const fetchEmployees = (inputValue, callback) => {
    debounceIndexEmployeesRequest({
      dispatch,
      params: { query: inputValue, active: true, display_length: 100 },
      successCallback: response => {
        callback(response.data.data.map(e => ({ label: e.full_name, value: e.id })));
      }
    });
  };

  return (
    <>
      <Row className="mt-4">
        <Col>
          <h2 className="mt-3">Programa de Capacitación</h2>
        </Col>
      </Row>
      <Accordion className="my-4" activeKey={activeAccordion} onSelect={eventKey => setActiveAccordion(eventKey)}>
        <Card className="mb-2">
          <Col className="top-header-employee py-2">
            <Accordion.Toggle
              as={Col}
              eventKey="0"
              className="card-header-title d-flex justify-content-between align-items-center"
              onClick={() => setActiveAccordion(activeAccordion === '0' ? null : '0')}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <span>Filtros de Búsqueda</span>
              <span className="h4 mr-2">{activeAccordion === '0' ? '−' : '+'}</span>
            </Accordion.Toggle>
          </Col>
          <Accordion.Collapse eventKey="0">
            <Card.Body className="employee-filters">
              <div className="employee-search-form px-4 py-2">
                <Row>
                  <Col md={3}>
                    <InputSelect
                      abbr
                      label="Año"
                      request={(inputValue, callback) => callback(yearOptions())}
                      defaultOptions={yearOptions()}
                      value={year}
                      onChange={setYear}
                      placeholder="Seleccionar año"
                    />
                  </Col>
                  <Col md={3}>
                    <InputSelect
                      label="Periodo"
                      request={(inputValue, callback) => callback(periods)}
                      defaultOptions={periods}
                      value={period}
                      onChange={setPeriod}
                      placeholder="Seleccionar periodo"
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <span>Rut</span>
                      </Form.Label>
                      <Form.Control
                        value={rut}
                        onChange={e => setRut(e.target.value)}
                        placeholder="Sin puntos y sin guion"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <InputSelect
                      isMulti
                      label="Colaboradores"
                      request={fetchEmployees}
                      onChange={data => setEmployees(data?.map(i => i.value) || [])}
                      placeholder="Seleccione colaboradores"
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md={3}>
                    <InputSelect
                      isMulti
                      label="Cargo"
                      request={fetchJobTitles}
                      onChange={data => setJobTitles(data?.map(i => i.value) || [])}
                      placeholder="Seleccionar Cargo"
                    />
                  </Col>
                  <Col md={3}>
                    <InputSelect
                      isMulti
                      label="Area"
                      request={fetchJobManagements}
                      onChange={data => setJobManagements(data?.map(i => i.value) || [])}
                      placeholder="Seleccionar area"
                    />
                  </Col>
                  <Col md={3}>
                    <InputSelect
                      isMulti
                      label="Centro de costo"
                      request={fetchCostCenters}
                      onChange={data => setCostCenters(data?.map(i => i.value) || [])}
                      placeholder="Seleccionar centro de costo"
                    />
                  </Col>
                  <Col md={3}>
                    <InputSelect
                      isMulti
                      label="Jefe Directo"
                      request={fetchEmployees}
                      onChange={data => setDirectLeaders(data?.map(i => i.value) || [])}
                      placeholder="Seleccione jefes"
                    />
                  </Col>
                </Row>
                <Row className="mt-3 pb-3">
                  <Col className="text-right">
                    <Button
                      onClick={() => handleIndexRequest({ display_start: 0 })}
                      variant="primary"
                      style={{ width: '150px' }}
                    >
                      FILTRAR
                    </Button>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <Row>
        <Col md={12} className="data-table-yellow">
          <TrainingProgramDatatable
            onRequest={onRequest}
            handleIndexRequest={handleIndexRequest}
            query={query}
            employeeDncs={employeeDncs}
            amount={amount}
          />
        </Col>
      </Row>
    </>
  );
};

export default TrainingProgramIndex;
